import { Button, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import type { AgentFormState } from "./AgentEdit";
import AgentEdit from "./AgentEdit";
import { useAgent } from "./useAgent";

interface ExistingAgentEditProps {
  value: AgentFormState;
  onSubmit: (state: AgentFormState) => Promise<void>;
  onDelete: () => Promise<void>;
  onCancel: () => void;
}

const ExistingAgentEdit: FC<ExistingAgentEditProps> = ({ value, onCancel, onDelete, onSubmit }) => {
  const { control, handleSubmit, watch } = useForm<AgentFormState>({
    defaultValues: value,
    mode: "onBlur",
  });
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);

  return (
    <>
      <AgentEdit control={control} isAdmin={watch("isAdmin")} />
      <Grid size={12} container sx={{ justifyContent: "flex-end", alignItems: "start" }}>
        <Stack direction="row" spacing={2}>
          <CancelButton onClick={onCancel} />
          <Button variant="outlined" color="error" onClick={() => setConfirmationDialogVisible(true)}>
            Usuń
          </Button>
          <AsyncLoadingButton variant="outlined" onClick={handleSubmit((s) => onSubmit(s))}>
            Zapisz
          </AsyncLoadingButton>
          <ConfirmationDialog
            open={confirmationDialogVisible}
            title="Usuń przedstawiciela"
            content="Czy chcesz usunąć przedstawiciela? Tej operacji nie będzie można już cofnąć."
            onCancel={() => setConfirmationDialogVisible(false)}
            onConfirm={onDelete}
          />
        </Stack>
      </Grid>
    </>
  );
};

const EditAgentPage = () => {
  const navigate = useNavigate();
  const { agent: value, isLoading, updateAgent, deleteAgent } = useAgent();

  const handleSubmit = async (state: AgentFormState) => {
    await updateAgent(value!.id, {
      name: state.name,
      contact: {
        przezmorzeEmail: state.przezmorzeEmail,
        b2bEmail: state.b2bEmail,
        phone: state.phone,
      },
      loginEmail: state.loginEmail,
      privileges: state.privileges,
      isAdmin: state.isAdmin,
      department: state.department ? state.department : undefined,
    });
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }
  if (!value) {
    return (
      <Grid container sx={{ marginTop: 2, marginBottom: 2 }}>
        <Typography variant="h5">Taki przedstawiciel nie istnieje</Typography>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ marginTop: 1, marginBottom: 1, paddingLeft: 1, paddingRight: 1 }}>
      <ExistingAgentEdit
        value={{
          name: value.name,
          przezmorzeEmail: value.contact.przezmorzeEmail ?? "",
          b2bEmail: value.contact.b2bEmail ?? "",
          loginEmail: value.loginEmail ?? "",
          phone: value.contact.phone ?? "",
          privileges: value.privileges ?? {
            onlyOwnCustomersOrders: false,
            onlyPublicAttachments: false,
            onlyOwnCustomersEdit: false,
            cannotChangeOptionPrices: false,
            cannotExportOrders: false,
            canViewProvidersAndProducts: false,
            canChangeCustomerAccountManager: false,
            canEditProvidersProductsAndDictionaries: false,
            canEditPriceLists: false,
            canEditPackages: false,
            canDeleteCustomerNotes: false,
            cannotViewCustomers: false,
            canViewStatistics: false,
          },
          isAdmin: value.isAdmin ?? false,
          department: value.department ?? "",
        }}
        onSubmit={handleSubmit}
        onDelete={deleteAgent}
        onCancel={() => navigate("/admin/agents")}
      />
    </Grid>
  );
};

export default EditAgentPage;
